import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { User, AuthState, LoginCredentials, RegisterData } from '../types';
import { toDate } from '../utils/formatTimeAgo';
import { apiLogin, apiRegister, apiMe, apiVerifyEmail, apiResendCode, apiForgotPassword, apiResetPassword, setAuthHandlers } from '../services/api';

const normalizeUser = (u: any): User => ({
  ...u,
  interests: u?.interests ?? [],
  clubs: u?.clubs ?? [],
  courses: u?.courses ?? [],
  friendsCount: u?.friendsCount ?? 0,
  createdAt: toDate(u?.createdAt) || new Date(),
  updatedAt: toDate(u?.updatedAt) || new Date(),
});

type LoginResult = { ok: boolean; needsVerification?: boolean; email?: string };
type RegisterResult = { ok: boolean; requiresVerification?: boolean; email?: string };
type VerifyResult = { ok: boolean; error?: string; alreadyVerified?: boolean };
type ResetResult = { ok: boolean; error?: string };

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<LoginResult>;
  register: (data: RegisterData) => Promise<RegisterResult>;
  verifyEmail: (email: string, code: string) => Promise<VerifyResult>;
  resendCode: (email: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<{ ok: boolean }>;
  resetPassword: (token: string, password: string) => Promise<ResetResult>;
  logout: () => void;
  updateProfile: (userData: Partial<User>) => Promise<void>;
  refreshSession: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_LEGAL_REQUIRED'; payload: { required: boolean; currentVersions?: User['legalCurrentVersions'] } }
  | { type: 'LOGOUT' };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_USER':
      return { ...state, user: action.payload, error: null };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_LEGAL_REQUIRED':
      return state.user
        ? {
            ...state,
            user: {
              ...state.user,
              legalAcceptanceRequired: action.payload.required,
              legalCurrentVersions: action.payload.currentVersions ?? state.user.legalCurrentVersions,
            },
          }
        : state;
    case 'LOGOUT':
      return { ...state, user: null, error: null };
    default:
      return state;
  }
};

const initialState: AuthState = {
  user: null,
  loading: true,
  error: null,
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const hydrateFromMe = async () => {
    const me = await apiMe();
    const baseUser = me.data || me.user || me;
    const user = normalizeUser({
      ...baseUser,
      legalAcceptanceRequired: Boolean(me.legal?.requiresAcceptance),
      legalCurrentVersions: me.legal?.currentVersions,
    });

    if (!localStorage.getItem('dragonsframe_token') && me?.token) {
      localStorage.setItem('dragonsframe_token', me.token);
    }

    localStorage.setItem('dragonsframe_user', JSON.stringify(user));
    dispatch({ type: 'SET_USER', payload: user });
  };

  // Wire api.ts global auth handlers to context state.
  // api.ts lives outside React/Router, so it calls these on 401/428 instead of navigating itself:
  //   401 (token present) -> clear session; ProtectedRoute then redirects to /login.
  //   428 -> flag legal re-acceptance; ProtectedRoute then redirects to /legal/acceptance.
  useEffect(() => {
    setAuthHandlers({
      onUnauthorized: () => {
        localStorage.removeItem('dragonsframe_token');
        localStorage.removeItem('dragonsframe_user');
        dispatch({ type: 'LOGOUT' });
      },
      onLegalRequired: (currentVersions) => {
        dispatch({ type: 'SET_LEGAL_REQUIRED', payload: { required: true, currentVersions } });
      },
    });
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        await hydrateFromMe();
      } catch (error) {
        const status = (error as any)?.code;
        if (status !== 401) {
          console.error('Auth check failed:', error);
        }
        localStorage.removeItem('dragonsframe_token');
        localStorage.removeItem('dragonsframe_user');
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    checkAuth();
  }, []);

  const login = async (credentials: LoginCredentials): Promise<LoginResult> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      const resp = await apiLogin(credentials);
      localStorage.setItem('dragonsframe_token', resp.token);
      await hydrateFromMe();
      return { ok: true };
    } catch (error: any) {
      if (error?.code === 403 && error?.info?.error === 'email_not_verified') {
        return { ok: false, needsVerification: true, email: error?.info?.email };
      }
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Login failed' });
      return { ok: false };
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const register = async (data: RegisterData): Promise<RegisterResult> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      const resp: any = await apiRegister(data);
      if (resp?.requiresVerification) {
        return { ok: true, requiresVerification: true, email: resp.email };
      }
      // Legacy path (verification disabled): log straight in.
      if (resp?.token) {
        localStorage.setItem('dragonsframe_token', resp.token);
        await hydrateFromMe();
      }
      return { ok: true };
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Registration failed' });
      return { ok: false };
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const verifyEmail = async (email: string, code: string): Promise<VerifyResult> => {
    try {
      const resp: any = await apiVerifyEmail(email, code);
      // Already-verified accounts get no session from this endpoint (it doesn't check a
      // password) — send the user to log in instead of trying to hydrate a session.
      if (resp?.alreadyVerified && !resp?.token) {
        return { ok: true, alreadyVerified: true };
      }
      if (resp?.token) localStorage.setItem('dragonsframe_token', resp.token);
      await hydrateFromMe();
      return { ok: true };
    } catch (error: any) {
      return { ok: false, error: error?.info?.error || (error instanceof Error ? error.message : 'verification_failed') };
    }
  };

  const resendCode = async (email: string): Promise<void> => {
    try { await apiResendCode(email); } catch { /* resend is always best-effort */ }
  };

  // Always resolves ok — the API never reveals whether the email has an account.
  const requestPasswordReset = async (email: string): Promise<{ ok: boolean }> => {
    try { await apiForgotPassword(email); } catch { /* best-effort, no enumeration */ }
    return { ok: true };
  };

  const resetPassword = async (token: string, password: string): Promise<ResetResult> => {
    try {
      const resp: any = await apiResetPassword(token, password);
      if (resp?.token) localStorage.setItem('dragonsframe_token', resp.token);
      await hydrateFromMe();
      return { ok: true };
    } catch (error: any) {
      return { ok: false, error: error?.info?.error || (error instanceof Error ? error.message : 'reset_failed') };
    }
  };

  const logout = () => {
    localStorage.removeItem('dragonsframe_token');
    localStorage.removeItem('dragonsframe_user');
    dispatch({ type: 'LOGOUT' });
  };

  const clearError = useCallback(() => {
    dispatch({ type: 'SET_ERROR', payload: null });
  }, []);

  const updateProfile = async (userData: Partial<User>) => {
    try {
      if (!state.user) throw new Error('No user logged in');

      const updatedUser = { ...state.user, ...userData, updatedAt: new Date() };
      localStorage.setItem('dragonsframe_user', JSON.stringify(updatedUser));
      dispatch({ type: 'SET_USER', payload: updatedUser });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Profile update failed' });
    }
  };

  const refreshSession = async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      await hydrateFromMe();
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    register,
    verifyEmail,
    resendCode,
    requestPasswordReset,
    resetPassword,
    logout,
    updateProfile,
    refreshSession,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 